import React, { useState } from 'react';
import styled from 'styled-components';

import { v4 as uuidv4 } from 'uuid';
import { Command, tabActions } from '@store/index';
import { Card } from '@common/Card';
import { useDispatch } from 'react-redux';
import { useTheme } from '../../../../../styles';
import { PublicOutlined } from '@mui/icons-material';
import { NodeField, NodeInput, NodeInputType } from '@components/Flow/Nodes';
import { NodeInputExpression } from '../../NodeInput';
import { RequestParametersExpression } from '../../Custom/RequestParametersExpression';
import { Alert, capitalize, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Label } from '@common/Label';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { Secrets } from '@common/Autocomplete/Secrets';
import { JSONEditor } from '@common/JSONEditor/JSONEditor';
import { useNodeData } from '@common/hooks/useNodeData';
import { useSecrets } from '@common/hooks/useSecrets';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const HTTPRequest: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { getOptions, dataEdges } = useNodeData();
  const { secrets, loading, refetch } = useSecrets();

  const dataOptions = getOptions(command.node.id, true);
  const state = command.state[field.id];

  const method = state.method;
  const url = state.url;
  const query_parameters = state.query_parameters;
  const header_parameters = state.header_parameters;
  const body_parameters = state.body_parameters;
  const body_format = state.body_format;
  const body_json = state.body_json;
  const auth_enabled = state.auth_enabled;
  const auth_secret_name = state.auth_secret_name;
  const enabled = state.enabled;

  const secret = secrets.find((secret) => secret.identifier === auth_secret_name);
  const secretType = secret?.type;

  const [collapsed, setCollapsed] = useState(!enabled);

  const toggleEnabled = (newVal: boolean) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );

    if (newVal) setCollapsed(false);
  };

  const updateField = (nestedFieldName: string, newValue: string, fieldType?: string) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName,
        value: newValue,
        fieldName: fieldType,
      })
    );
  };

  const addParameterField = (nestedFieldName: string) => {
    const inputValue: NodeInput<string> = { type: NodeInputType.Expression, value: '' };
    const value = { id: uuidv4(), name: '', value: inputValue };

    const input = {
      nodeId: command.node.id,
      fieldId: field.id,
      nestedFieldName,
      value: value,
    };

    dispatch(tabActions.nodeInputNestedArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const updateParameterField = (
    nestedFieldName: string,
    fieldType: string,
    nestedFieldId: string,
    newValue: string
  ) => {
    const input = {
      nodeId: command.node.id,
      fieldId: field.id,
      nestedFieldName,
      nestedFieldId,
      fieldName: fieldType,
      value: fieldType === 'value' ? { type: NodeInputType.Expression, value: newValue } : newValue,
    };

    dispatch(tabActions.nodeInputNestedArrayUpdate(input));
    dispatch(tabActions.setSaved(false));
  };

  const deleteParameterField = (nestedFieldName: string, nestedFieldId: string) => {
    dispatch(
      tabActions.nodeInputNestedArrayFilter({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId,
        nestedFieldName,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const methodOptions = [
    { label: 'GET', value: 'get' },
    { label: 'POST', value: 'post' },
  ];

  const body = (
    <Container>
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={methodOptions}
        override={{
          title: 'Method',
          tooltip: 'The HTTP method to use for this request.',
          value: method,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'method',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'URL',
          tooltip: 'The URL to send the request to.',
          placeholder: 'https://example.com',
          value: url,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'url',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <Stack spacing={1}>
        <Label title='Authentication' tooltip='Set to true if you want to send auth credentials with this request.' />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={auth_enabled}
          exclusive
          onChange={(_, newVal) => newVal !== undefined && newVal !== null && updateField('auth_enabled', newVal)}
          fullWidth
        >
          <ToggleButton size='small' value={true}>
            Auth
          </ToggleButton>
          <ToggleButton size='small' value={false}>
            No Auth
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {auth_enabled && Object.keys(secrets).length === 0 && (
        <Alert
          severity='warning'
          action={
            <Button onClick={() => navigate(`/${service_id}/secrets`)} color='inherit' small customColor='orange'>
              Create
            </Button>
          }
        >
          You do not have any secrets.
        </Alert>
      )}
      {auth_enabled && Object.keys(secrets).length > 0 && (
        <Stack spacing={1}>
          <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
            <Label
              title='Secret'
              tooltip='Select the secret to be sent as the authentication. The type of authentication used is dependent on the type of secret selected (bearer or basic).'
            />
            {secretType && (
              <Chip
                color={secretType === 'bearer' ? 'info' : 'primary'}
                label={capitalize(secretType)}
                size='small'
                variant='outlined'
                sx={{ fontSize: '1.3ch', height: '19px !important' }}
              />
            )}
          </Stack>
          <Secrets
            refetch={refetch}
            value={auth_secret_name}
            loading={loading}
            secrets={secrets}
            onChange={(_, item) => {
              if (!item?.value) return;
              updateField('auth_secret_name', item.value.identifier);
            }}
            placeholder='Secret'
          />
        </Stack>
      )}
      <Divider />
      <RequestParametersExpression
        command={command}
        field={field}
        stateOverride={{
          value: query_parameters,
          title: 'Query Parameters',
          tooltip: 'Add query parameters to the request.',
          addField: () => addParameterField('query_parameters'),
          updateField: (nestedFieldId, fieldType, newValue) =>
            updateParameterField('query_parameters', fieldType, nestedFieldId, newValue),
          deleteField: (nestedFieldId) => deleteParameterField('query_parameters', nestedFieldId),
          allowToolArgs: true,
        }}
      />
      <RequestParametersExpression
        command={command}
        field={field}
        stateOverride={{
          value: header_parameters,
          title: 'Headers',
          tooltip: 'Add headers to the request.',
          addField: () => addParameterField('header_parameters'),
          updateField: (nestedFieldId, fieldType, newValue) =>
            updateParameterField('header_parameters', fieldType, nestedFieldId, newValue),
          deleteField: (nestedFieldId) => deleteParameterField('header_parameters', nestedFieldId),
          allowToolArgs: true,
        }}
      />
      {method?.value === 'post' && (
        <Stack spacing={1}>
          <Label
            title='Body Format'
            tooltip='Specify how to format the body of the request. The "FORM" input type is used for simple values, and the "JSON" input type is used for complex data structures like a nested object.'
          />
          <ToggleButtonGroup
            size='small'
            color='primary'
            value={body_format}
            exclusive
            onChange={(_, newVal) => newVal !== undefined && newVal !== null && updateField('body_format', newVal)}
            fullWidth
          >
            <ToggleButton size='small' value='form'>
              Form
            </ToggleButton>
            <ToggleButton size='small' value='json'>
              JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      {method?.value === 'post' && body_format === 'form' && (
        <RequestParametersExpression
          command={command}
          field={field}
          stateOverride={{
            value: body_parameters,
            title: 'Body Parameters',
            tooltip: 'Add body parameters to the request.',
            addField: () => addParameterField('body_parameters'),
            updateField: (nestedFieldId, fieldType, newValue) =>
              updateParameterField('body_parameters', fieldType, nestedFieldId, newValue),
            deleteField: (nestedFieldId) => deleteParameterField('body_parameters', nestedFieldId),
            allowToolArgs: true,
          }}
        />
      )}
      {method?.value === 'post' && body_format === 'json' && (
        <Stack spacing={1}>
          <Label
            title='Body Parameters'
            tooltip='Define the output schema for your request body. The output must be a valid JSON object.'
          />
          <JSONEditor
            value={body_json?.value || ''}
            setValue={(newValue: string) => updateField('body_json', newValue, 'value')}
            options={dataOptions}
            dataEdges={dataEdges}
          />
        </Stack>
      )}
    </Container>
  );

  return (
    <Card
      title='HTTP Request'
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      enabled={enabled}
      setEnabled={toggleEnabled}
      icon={<PublicOutlined sx={{ color: theme.telnyx.blue, fontSize: '18px' }} />}
      description={field.description}
      descriptionBorder
      body={body}
    />
  );
};
